const toFixed = (value: any, decimalPlaces: any): string  => {
  const decimalPlacesInt = parseInt(decimalPlaces, 10);
  if (invalidInputs(value, decimalPlacesInt)) {
    return isNaN(decimalPlacesInt) ? value : '';
  }

  return numberToFixed(value.toString(), decimalPlacesInt);
};

const invalidInputs = (value: any, decimalPlaces: any): boolean  => {
  return value === null ||
      value.toString().length === 0 ||
      isNaN(Number(value)) ||
      isNaN(decimalPlaces);
};

const numberToFixed = (value: string, decimalPlaces: number): string  => {
  const [integerPart, fractionalPart] = value.split('.', 2);
  const [fractionalResult, overflow] = handleFractionalPart(fractionalPart, decimalPlaces);
  const integerResult = handleIntegerPart(integerPart, overflow);
  return integerResult + fractionalResult;
};

const handleFractionalPart = (fractionalPart, decimalPlaces: number): [string, number] => {
  let fractionalResult = decimalPlaces === 0 ? '' : '.';
  let overflow = 0;

  if (fractionalPart === undefined) {
    fractionalResult += '0'.repeat(decimalPlaces);
    return [fractionalResult, overflow];
  } else if (fractionalPart.length > decimalPlaces) {
    let trimmedResult;
    [trimmedResult, overflow] = handleRounding(fractionalPart, decimalPlaces);
    fractionalResult += trimmedResult;
    return [fractionalResult, overflow];
  } else {
    fractionalResult += fractionalPart + '0'.repeat(decimalPlaces - fractionalPart.length);
    return [fractionalResult, overflow];
  }
};

const handleRounding = (fractionalPart, decimalPlaces: number): [string, number] => {
  /**
   *  This function takes a string of digits that has more than decimalPlaces characters and returns a string,
   *  It returns a string of digits with decimalPlaces characters that is rounded consistently and a 0 or 1 depending if rounding the string overflowed.
   *
   * We want this to be consistent,that is:
   *  If the part to be rounded is exactly halfway between two legitimate values (eg 2.35 (1dp) is halfway between 2.3and 2.4)
   *  Then it is always rounded up. This is why we CANNOT use the inbuilt toFixed() (as (2.35).toFixed(1) = '2.4' but (2.55).toFixed(1) = '2.5')
   *
   *  We start with the following idea:
   *    fractionalResult =  fractionalPart.substring(0,decimalPlaces) + '.' + fractionalPart.substring(decimalPlaces); - Make the  fractional part into a number with a decimal place in the right part,
   *    fractionalResult =  Math.round(Number(fractionalResult)).toString(); round that number and then convert back to a string.
   *
   * This idea has two problems:
   *  leading zeroes, and tracking overflow.
   *
   *  leading zeroes:
   *    If fractional part has leading zeroes (say 0.000345) when we make it into a number the leading zeroes will be lost.
   *    eg .000345 to 4dp = .0003 but without something accounting for leading zeros making 0003.45 into a number will return 3.45 which loses the leading zeroes
   *  Overflow:
   *    Some numbers overflow when rounded. This must be tracked and passed back.
   *    eg .99999987 to 4dp = 1.0000 but without something accounting for overflow, rounding 9999.987 will return 10000 and our resulting string will be both the wrong length and value.
   *
   * We handle these issues by prepending our fractional result with the string '1' before converting it into a number.
   * thus, leading zeroes are not stripped, and  we can track overflow.
   * Additionally we add the following lines to the function:
   *   const overflow = Number(fractionalResult[0]) - 1; - check if overflow occurred
   *   eg .99999987 to 4dp becomes 19999.987 and is rounded to 20000 so Number(fractionalResult[0]) = 2 and overflow = 1
   *
   *   fractionalResult = fractionalResult.substring(1); - remove the string we prepended.
   */

  let fractionalResult = '1' + fractionalPart.substring(0,decimalPlaces) + '.' + fractionalPart.substring(decimalPlaces);
  fractionalResult =  Math.round(Number(fractionalResult)).toString();
  const overflow = Number(fractionalResult[0]) - 1;
  fractionalResult = fractionalResult.substring(1);
  return [fractionalResult, overflow];
};

const handleIntegerPart = (integerPart: string, overflow: number): string => {
  const sign = Number(integerPart) < 0 ? -1 : 1;
  return (Number(integerPart) + overflow * sign).toString();
};

export default toFixed;